import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ServiceProvider {
  id: string;
  name: string;
  avatarInitials: string;
  avatarUrl?: string;
  category: string;
}

export function useServiceProviders() {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchServiceProviders = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, fetch services with categories
        const { data: services, error: servicesError } = await supabase
          .from('services')
          .select(`
            user_id,
            service_categories:category_id (
              name
            )
          `)
          .limit(20); // Get more than we need for better randomization

        if (servicesError) {
          throw servicesError;
        }

        if (!services || services.length === 0) {
          setProviders([]);
          return;
        }

        // Get unique user IDs
        const uniqueUserIds = [...new Set(services.map(service => service.user_id))];
        
        // Fetch public profile data for each unique user using the secure function
        const profilePromises = uniqueUserIds.map(userId => 
          supabase.rpc('get_public_profile', { profile_user_id: userId })
        );
        
        const profileResults = await Promise.all(profilePromises);
        
        // Create a map of user profiles
        const profileMap = new Map();
        profileResults.forEach((result, index) => {
          if (result.data && result.data.length > 0) {
            profileMap.set(uniqueUserIds[index], result.data[0]);
          }
        });

        // Process and deduplicate providers
        const uniqueProviders = new Map<string, ServiceProvider>();
        
        services.forEach(service => {
          if (service.user_id && profileMap.has(service.user_id)) {
            const profile = profileMap.get(service.user_id);
            const categoryName = service.service_categories?.name || 'General';
            
            // Only include providers with both first and last names
            if (profile.first_name && profile.last_name) {
              const fullName = `${profile.first_name} ${profile.last_name}`;

              // Generate avatar initials from first and last name
              const avatarInitials = `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();

              // Use user_id as key to avoid duplicates
              if (!uniqueProviders.has(service.user_id)) {
                uniqueProviders.set(service.user_id, {
                  id: service.user_id,
                  name: fullName,
                  avatarInitials,
                  avatarUrl: profile.avatar_url || undefined,
                  category: categoryName
                });
              }
            }
          }
        });

        setProviders(Array.from(uniqueProviders.values()));
      } catch (err) {
        console.error('Error fetching service providers:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch service providers');
        setProviders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceProviders();
  }, []);

  // Function to generate random positions around the image perimeter that work on all screen sizes
  const generateRandomPerimeterPositions = (count: number): any[] => {
    const positions: any[] = [];
    
    // Get current screen size for responsive positioning
    const screenWidth = window.innerWidth;
    const isMobile = screenWidth < 768;
    const isTablet = screenWidth >= 768 && screenWidth < 1024;
    
    // Responsive card dimensions
    const cardWidth = isMobile ? 140 : 170;
    const cardHeight = isMobile ? 50 : 60;
    
    // Calculate image container dimensions based on screen size
    let imageContainerWidth: number;
    let imageContainerHeight: number;
    
    if (isMobile) {
      imageContainerWidth = Math.min(screenWidth * 0.9, 400);
    } else if (isTablet) {
      imageContainerWidth = Math.min(screenWidth * 0.5, 600);
    } else {
      imageContainerWidth = Math.min(screenWidth * 0.45, 800);
    }
    
    imageContainerHeight = imageContainerWidth * 0.6; // Maintain aspect ratio
    
    // Define perimeter zones with screen-size specific constraints
    let perimeterZones: any[];
    
    if (isMobile) {
      // Mobile: cards even closer to edges
      perimeterZones = [
        { type: 'top', topRange: [0, 2], leftRange: [2, 75] },
        { type: 'bottom', bottomRange: [0, 2], leftRange: [2, 75] },
        { type: 'left', topRange: [20, 60], leftRange: [0, 2] },
        { type: 'right', topRange: [20, 60], rightRange: [0, 2] }
      ];
    } else if (isTablet) {
      // Tablet: closer to edges
      perimeterZones = [
        { type: 'top', topRange: [0, 3], leftRange: [3, 78] },
        { type: 'bottom', bottomRange: [0, 3], leftRange: [3, 78] },
        { type: 'left', topRange: [15, 65], leftRange: [0, 3] },
        { type: 'right', topRange: [15, 65], rightRange: [0, 3] }
      ];
    } else {
      // Desktop: much closer to edges
      perimeterZones = [
        { type: 'top', topRange: [0, 3], leftRange: [2, 80] },
        { type: 'bottom', bottomRange: [0, 4], leftRange: [2, 80] },
        { type: 'left', topRange: [15, 70], leftRange: [0, 3] },
        { type: 'right', topRange: [15, 70], rightRange: [0, 3] }
      ];
    }

    // Helper function to convert percentage to pixel coordinates
    const getPixelPosition = (pos: any) => {
      let x = 0, y = 0;
      
      if (pos.left !== undefined) {
        x = (parseFloat(pos.left) / 100) * imageContainerWidth;
      } else if (pos.right !== undefined) {
        x = imageContainerWidth - (parseFloat(pos.right) / 100) * imageContainerWidth - cardWidth;
      }
      
      if (pos.top !== undefined) {
        y = (parseFloat(pos.top) / 100) * imageContainerHeight;
      } else if (pos.bottom !== undefined) {
        y = imageContainerHeight - (parseFloat(pos.bottom) / 100) * imageContainerHeight - cardHeight;
      }
      
      return { x, y, width: cardWidth, height: cardHeight };
    };

    // Enhanced collision detection with responsive padding
    const isOverlapping = (pos1: any, pos2: any): boolean => {
      const rect1 = getPixelPosition(pos1);
      const rect2 = getPixelPosition(pos2);
      
      // Responsive padding based on screen size
      const padding = isMobile ? 15 : 20;
      
      return !(
        rect1.x + rect1.width + padding < rect2.x ||
        rect2.x + rect2.width + padding < rect1.x ||
        rect1.y + rect1.height + padding < rect2.y ||
        rect2.y + rect2.height + padding < rect1.y
      );
    };

    // Helper function to check if position is within safe bounds
    const isWithinBounds = (position: any): boolean => {
      const pixel = getPixelPosition(position);
      
      return (
        pixel.x >= 0 &&
        pixel.y >= 0 &&
        pixel.x + pixel.width <= imageContainerWidth &&
        pixel.y + pixel.height <= imageContainerHeight
      );
    };

    // Generate positions for each card with improved responsive logic
    for (let i = 0; i < count; i++) {
      let newPosition: any;
      let validPosition = false;
      
      // Try multiple zones if current one doesn't work
      const shuffledZones = [...perimeterZones].sort(() => Math.random() - 0.5);
      
      for (const zone of shuffledZones) {
        if (validPosition) break;
        
        // Try multiple positions within this zone
        for (let zoneAttempts = 0; zoneAttempts < 25; zoneAttempts++) {
          newPosition = { className: "" };
          
          // Generate position based on zone type with responsive constraints
          switch (zone.type) {
            case 'top':
              newPosition.top = `${zone.topRange[0] + Math.random() * (zone.topRange[1] - zone.topRange[0])}%`;
              newPosition.left = `${zone.leftRange[0] + Math.random() * (zone.leftRange[1] - zone.leftRange[0])}%`;
              break;
            case 'bottom':
              newPosition.bottom = `${zone.bottomRange[0] + Math.random() * (zone.bottomRange[1] - zone.bottomRange[0])}%`;
              newPosition.left = `${zone.leftRange[0] + Math.random() * (zone.leftRange[1] - zone.leftRange[0])}%`;
              break;
            case 'left':
              newPosition.top = `${zone.topRange[0] + Math.random() * (zone.topRange[1] - zone.topRange[0])}%`;
              newPosition.left = `${zone.leftRange[0] + Math.random() * (zone.leftRange[1] - zone.leftRange[0])}%`;
              break;
            case 'right':
              newPosition.top = `${zone.topRange[0] + Math.random() * (zone.topRange[1] - zone.topRange[0])}%`;
              newPosition.right = `${zone.rightRange[0] + Math.random() * (zone.rightRange[1] - zone.rightRange[0])}%`;
              break;
          }
          
          // Check if position is valid (within bounds and no overlap)
          if (
            isWithinBounds(newPosition) &&
            !positions.some(existingPos => isOverlapping(newPosition, existingPos))
          ) {
            validPosition = true;
            break;
          }
        }
      }
      
      // Fallback position if no valid position found
      if (!validPosition) {
        console.warn(`Could not find non-overlapping position for card ${i + 1}, using fallback position`);
        newPosition = {
          top: `${5 + (i * 12)}%`,
          left: `${5 + (i * 8)}%`,
          className: ""
        };
      }
      
      if (newPosition) {
        positions.push(newPosition);
      }
    }
    
    console.log(`Generated ${positions.length} responsive positions for ${isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'} screen`);
    return positions;
  };

  // Function to get random providers with randomized perimeter positioning
  const getRandomProviders = (count: number = 5): (ServiceProvider & { position: any; key: string })[] => {
    if (providers.length === 0) return [];
    
    // Shuffle providers and take exactly the count requested (up to 5)
    const shuffled = [...providers].sort(() => Math.random() - 0.5);
    const selectedProviders = shuffled.slice(0, Math.min(count, Math.min(5, providers.length)));

    // Generate random perimeter positions
    const randomPositions = generateRandomPerimeterPositions(selectedProviders.length);

    return selectedProviders.map((provider, index) => ({
      ...provider,
      position: randomPositions[index],
      key: `${provider.id}-${Math.random()}`
    }));
  };

  return {
    providers,
    loading,
    error,
    getRandomProviders,
    hasProviders: providers.length > 0
  };
}
